import {useLazyQuery} from "@apollo/client";
import debounce from "lodash/debounce";
import {ForwardedRef, forwardRef, useCallback} from "react";
import {components, OptionProps, SelectInstance, SingleValueProps} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {CheckInfo, FindChecksResult, GQL_FIND_CHECKS} from "@/lib/queries/statuspages.ts";

const SingleValue = ({children, data, ...props}: SingleValueProps<CheckInfo>) => (
  <components.SingleValue {...props} data={data}>
    <span className="me-2">{children}</span>
    <small className="text-muted">{data.type}</small>
  </components.SingleValue>
);

const ServiceOption = ({data, ...props}: OptionProps<CheckInfo>) => (
  <components.Option {...props} data={data}>
    <div className="text-muted float-end">{data.type}</div>
    <div className="text-truncate">{data.name}</div>
    <div className="text-muted pulseTextBodyTiny">{data.address}</div>
  </components.Option>
);

type ServiceSelectProps = SelectProps<CheckInfo> & {
  serviceType?: string;
};

const ServiceSelect = forwardRef(
  (
    {serviceType, placeholder = "Select a Check..", ...props}: ServiceSelectProps,
    ref: ForwardedRef<SelectInstance<CheckInfo>>
  ) => {
    const [searchChecks] = useLazyQuery<FindChecksResult>(GQL_FIND_CHECKS);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOptions = useCallback(
      debounce((inputValue: string, setOptions: (options: CheckInfo[]) => void) => {
        searchChecks({
          variables: {
            search: inputValue,
            serviceType: serviceType || undefined,
            limit: 50,
          },
        }).then(({data}) => {
          setOptions(data?.findChecks?.items || []);
        });
      }, 300),
      [searchChecks, serviceType]
    );

    return (
      <Select
        ref={ref}
        id={props.name}
        async={true}
        isSearchable
        placeholder={placeholder}
        defaultOptions={true}
        loadOptions={loadOptions}
        getOptionValue={(c) => c.id.toString()}
        getOptionLabel={(c) => c.name}
        components={{Option: ServiceOption, SingleValue: SingleValue}}
        {...props}
      />
    );
  }
);

export default ServiceSelect;
